import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIResource } from "../../constants/application";
import { getUrl } from "utils/getURL";
import axiosInstance from "utils/axiosInstance";
import { AssociatedCustomerDataList } from "./validatePAN";
import { BANK_STATUS_VARIANTS } from "constants/bankStatusVariants";
import { AppSpecificName } from "constants/headerImage";
import { setGenericInformation } from "./genericInformation";
import { resolveKycErrorMessage } from "utils/kycErrorMessage";

export interface IValidateBankAPIData {
  customerId: string;
  bankAccountNumber: string;
  ifscCode: string;
  userProvidedName: string | null;
  accountType: string;
  customerSource: string;
  accountProofDocumentUrl?: string;
}

interface IBankResponse {
  accountType?: string;
  bankAccountNumber?: string;
  ifscCode?: string;
  bankName?: string;
  branchName?: string;
  bankAddress?: string;
  accountHolderName?: string;
  userNameMatchScore?: number;
  nameMatched?: boolean;
  associatedCustomerDataList?: AssociatedCustomerDataList[];
  accountProofDocumentUrl?: string;
  message?: string;
}

export interface IValidateBankResult {
  bankStatus: (typeof BANK_STATUS_VARIANTS)[keyof typeof BANK_STATUS_VARIANTS];
  bankResponse: IBankResponse | null;
  failureMessage?: string;
  failure?: IValidateBankFailure;
  isSuccess: boolean;
}

export interface IValidateBankFailure {
  errorCode?: string;
  message: string;
}

export interface IBankSlice {
  loading: boolean;
  error: string;
  success: boolean;
  bankResponse: null | IBankResponse;
  bankStatus: (typeof BANK_STATUS_VARIANTS)[keyof typeof BANK_STATUS_VARIANTS];
}

const defaultValues: IBankSlice = {
  loading: false,
  error: "",
  success: false,
  bankResponse: null,
  bankStatus: BANK_STATUS_VARIANTS.PENDING,
};

interface IValidateBank {
  data: IValidateBankAPIData;
}

const getBankFailureMessage = (errorCode?: string, fallbackMessage?: string) => {
  const normalizedErrorCode = String(errorCode || '').trim().toUpperCase();
  const defaultMessage = resolveKycErrorMessage(undefined, 'CHEQUE');
  const resolvedMessage = normalizedErrorCode
    ? resolveKycErrorMessage(normalizedErrorCode, 'CHEQUE')
    : '';

  if (normalizedErrorCode && resolvedMessage !== defaultMessage) {
    return resolvedMessage;
  }

  return String(fallbackMessage || resolvedMessage || defaultMessage).trim() || defaultMessage;
};

const createValidateBankFailure = (
  errorCode?: string,
  fallbackMessage?: string
): IValidateBankFailure => {
  const normalizedErrorCode = String(errorCode || '').trim().toUpperCase();

  return {
    errorCode: normalizedErrorCode || undefined,
    message: getBankFailureMessage(normalizedErrorCode, fallbackMessage),
  };
};

export const getValidateBankFailure = (value: unknown): IValidateBankFailure => {
  if (typeof value === 'string') {
    return createValidateBankFailure(undefined, value);
  }

  const candidate = value as {
    errorCode?: unknown;
    code?: unknown;
    message?: unknown;
    failure?: {
      errorCode?: unknown;
      message?: unknown;
    };
    response?: {
      data?: {
        error?: unknown;
        message?: unknown;
      };
    };
  } | null;

  const responseData = candidate?.response?.data;
  const failure = candidate?.failure;
  const errorCode = String(failure?.errorCode || candidate?.errorCode || candidate?.code || responseData?.error || '').trim().toUpperCase();
  const fallbackMessage = String(failure?.message || candidate?.message || responseData?.message || '').trim();

  return createValidateBankFailure(errorCode, fallbackMessage);
};

// Async thunk for Bank validation
export const ValidateBank = createAsyncThunk<
  IValidateBankResult,
  IValidateBank,
  { rejectValue: IValidateBankFailure }
>(
  "validateBank",
  async ({ data }: IValidateBank, thunkAPI) => {
    const { dispatch } = thunkAPI;
    try {
      const appName: any = localStorage.getItem('appname');
      data['customerSource'] = AppSpecificName[appName];

      const response = await axiosInstance.post(
        getUrl(APIResource.validateBank, [
          {
            variableName: "customerId",
            variableValue: data.customerId,
          },
        ]),
        {
          ...data,
        }
      );

      if (response.status === 200) {
        if (response?.data?.message) {
          dispatch(setBankResponse({ value: null }));
          dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
          dispatch(setGenericInformation({ bankDetails: null }));

          const failure = createValidateBankFailure(
            response?.data?.error,
            response?.data?.message
          );
          
          return {
            bankStatus: BANK_STATUS_VARIANTS.PENDING,
            bankResponse: null,
            failureMessage: failure.message,
            failure,
            isSuccess: false,
          } as IValidateBankResult;
        // } else if (response.data && response.data.associatedCustomerDataList && response.data.associatedCustomerDataList.length > 0) {
        //   dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_ASSOCIATION }));
        //   dispatch(setBankResponse({ value: response.data }));
        //   dispatch(setGenericInformation({ bankDetails: response.data }));
        //   return {
        //     bankStatus: BANK_STATUS_VARIANTS.BANK_ASSOCIATION,
        //     bankResponse: response.data,
        //     failureMessage: 'This bank account is linked to another customer ID.',
        //     isSuccess: false,
        //   } as IValidateBankResult;
        } else if (response?.data?.accountType) {
          dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_VERIFIED }));
          dispatch(setBankResponse({ value: response.data }));
          dispatch(setGenericInformation({ bankDetails: response.data }));
          return {
            bankStatus: BANK_STATUS_VARIANTS.BANK_VERIFIED,
            bankResponse: response.data,
            failureMessage: '',
            failure: undefined,
            isSuccess: true,
          } as IValidateBankResult;
        } else {
          dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_INACTIVE }));
          dispatch(setBankResponse({ value: null }));
          dispatch(setGenericInformation({ bankDetails: null }));
          const failure = createValidateBankFailure(undefined);
          return {
            bankStatus: BANK_STATUS_VARIANTS.BANK_INACTIVE,
            bankResponse: null,
            failureMessage: failure.message,
            failure,
            isSuccess: false,
          } as IValidateBankResult;
        }
      } else if (response.status === 400) {
          dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
          dispatch(setBankResponse({ value: null }));
          return thunkAPI.rejectWithValue(
            createValidateBankFailure("KYC-400")
          );
      } else {
        dispatch(setBankResponse({ value: null }));
        dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
        dispatch(setGenericInformation({ bankDetails: null }));
        const failure = createValidateBankFailure(undefined);
        return {
          bankStatus: BANK_STATUS_VARIANTS.PENDING,
          bankResponse: null,
          failureMessage: failure.message,
          failure,
          isSuccess: false,
        } as IValidateBankResult;
      }
    } catch (error: any) {
      dispatch(setBankResponse({ value: null }));
      dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
      dispatch(setGenericInformation({ bankDetails: null }));

      const failure = createValidateBankFailure(
        error?.response?.data?.error || error?.code,
        error?.response?.data?.message || error?.message
      );

      return {
        bankStatus: BANK_STATUS_VARIANTS.PENDING,
        bankResponse: null,
        failureMessage: failure.message,
        failure,
        isSuccess: false,
      } as IValidateBankResult;
    }
  }
);

// Slice definition
const BankSlice = createSlice({
  name: "validateBank",
  initialState: defaultValues,
  reducers: {
    setBankStatus: (state, action) => {
      state.bankStatus = action.payload.value;
    },
    setBankResponse: (state, action) => {
      state.bankResponse = action.payload.value;
    },
    setError: (state, action) => {
      state.error = action.payload.value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ValidateBank.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.success = false;
      })
      .addCase(ValidateBank.fulfilled, (state, action) => {
        const actionPayload = action.payload as IValidateBankResult | undefined;
        state.loading = false;
        state.error = actionPayload?.isSuccess ? "" : actionPayload?.failure?.message || actionPayload?.failureMessage || "";
        state.success = Boolean(actionPayload?.isSuccess);
      })
      .addCase(ValidateBank.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = getValidateBankFailure(action.payload).message;
      });
  },
});

// Export actions and reducer
export const { setError, setBankResponse, setBankStatus } = BankSlice.actions;
export default BankSlice.reducer;
